import React from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import './LandingPage.css';
import { useSeo } from '../hooks/useSeo';
import { useTranslation } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import NavDropdown from './NavDropdown';
import FaqItem from './FaqItem';

const TOOL_ROUTES = ['/generator-timova', '/raspored-turnira', '/semafor-online'];
const TOOL_ICONS = ['🎲', '📅', '⏱️'];
const GAME_ROUTES = ['/penalty-shootout', '/koji-si-igrac', '/nogomandala'];
const GAME_ICONS = ['11', '⚽', '🎯'];

const LandingPage = () => {
  const { t } = useTranslation();

  const toolsMenu = t('landing.toolsMenu').map(([label, description], index) => ({
    to: TOOL_ROUTES[index],
    icon: TOOL_ICONS[index],
    label,
    description,
  }));

  const gamesMenu = t('landing.gamesMenu').map(([label, description], index) => ({
    to: GAME_ROUTES[index],
    icon: GAME_ICONS[index],
    label,
    description,
  }));

  useSeo({
    title: t('landing.seoTitle'),
    description: t('landing.seoDescription'),
    canonical: 'https://statarena.app/',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/',
  });

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <span className="landing-logo-mark" role="img" aria-label={t('common.brandBall')}>⚽</span>
            <span className="landing-logo-text">StatArena</span>
          </div>
          <nav className="landing-nav-links">
            <a href="#mogucnosti">{t('nav.features')}</a>
            <a href="#kako-radi">{t('nav.howItWorks')}</a>
            <NavDropdown label={t('nav.tools')} items={toolsMenu} />
            <NavDropdown label={t('nav.games')} items={gamesMenu} />
            <LanguageSwitcher />
            <Link to="/login" className="landing-nav-login">
              {t('nav.login')}
            </Link>
            <Link to="/register" className="btn btn-primary landing-nav-cta">
              {t('nav.registerCta')}
            </Link>
          </nav>
        </div>
      </header>

      <section className="landing-hero">
        <span className="hero-eyebrow">{t('landing.eyebrow')}</span>
        <h1 className="landing-hero-title">
          {t('landing.titleStart')} <span className="accent">{t('landing.titleAccent')}</span>
        </h1>
        <p className="landing-hero-subtitle">{t('landing.subtitle')}</p>
        <div className="landing-hero-actions">
          <Link to="/register" className="btn btn-primary landing-hero-cta-main">
            {t('landing.registerFree')}
          </Link>
          <Link to="/directory" className="landing-hero-cta-ghost">
            {t('landing.viewTeams')}
          </Link>
        </div>
        <p className="landing-hero-note">{t('landing.heroNote')}</p>
      </section>

      <section id="mogucnosti" className="landing-section">
        <div className="section-header landing-section-header">
          <h2>{t('landing.featuresTitle')}</h2>
          <p className="landing-section-sub">{t('landing.featuresSub')}</p>
        </div>

        <div className="landing-features">
          {t('landing.features').map(([ariaLabel, icon, title, body]) => (
            <article className="landing-feature" key={title}>
              <div className="landing-feature-icon">
                <span role="img" aria-label={ariaLabel}>{icon}</span>
              </div>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="kako-radi" className="landing-section landing-section-alt">
        <div className="section-header landing-section-header">
          <h2>{t('landing.howTitle')}</h2>
          <p className="landing-section-sub">{t('landing.howSub')}</p>
        </div>

        <div className="landing-steps">
          {t('landing.steps').map(([title, body], index) => (
            <div className="landing-step" key={title}>
              <div className="landing-step-num">{index + 1}</div>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="za-koga" className="landing-section">
        <div className="section-header landing-section-header">
          <h2>{t('landing.audienceTitle')}</h2>
        </div>
        <div className="landing-audience">
          {t('landing.audience').map(([ariaLabel, icon, title, body]) => (
            <div className="landing-audience-card" key={title}>
              <h3><span role="img" aria-label={ariaLabel}>{icon}</span> {title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="landing-section landing-section-alt">
        <div className="section-header landing-section-header">
          <h2>{t('landing.faqTitle')}</h2>
          <p className="landing-section-sub">{t('landing.faqSub')}</p>
        </div>

        <div className="faq-list">
          {t('landing.faqs').map(([question, answer]) => (
            <FaqItem question={question} key={question}>
              <p>{answer}</p>
            </FaqItem>
          ))}
        </div>
      </section>

      <section className="landing-cta">
        <h2>{t('landing.ctaTitle')}</h2>
        <p>{t('landing.ctaText')}</p>
        <div className="landing-cta-actions">
          <Link to="/register" className="btn btn-primary btn-lg">
            {t('nav.registerCta')}
          </Link>
          <Link to="/login" className="btn btn-secondary btn-lg">
            {t('landing.haveAccount')}
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <div className="landing-footer-brand-row">
              <span className="landing-logo-mark" role="img" aria-label={t('common.brandBall')}>⚽</span>
              <span className="landing-logo-text">StatArena</span>
            </div>
            <p className="landing-footer-tag">{t('landing.footerTag')}</p>
          </div>

          <div className="landing-footer-links">
            <h4>{t('landing.app')}</h4>
            <a href="#mogucnosti">{t('nav.features')}</a>
            <a href="#kako-radi">{t('nav.howItWorks')}</a>
            <a href="#faq">{t('landing.faqShort')}</a>
          </div>

          <div className="landing-footer-links">
            <h4>{t('landing.toolsAndGames')}</h4>
            {toolsMenu.map((item) => (
              <Link to={item.to} key={item.to}>{item.label}</Link>
            ))}
            <Link to="/koji-si-igrac">{gamesMenu[1].label}</Link>
            <Link to="/nogomandala">{gamesMenu[2].label}</Link>
          </div>

          <div className="landing-footer-links">
            <h4>{t('landing.account')}</h4>
            <Link to="/login">{t('nav.login')}</Link>
            <Link to="/register">{t('nav.register')}</Link>
          </div>

          <div className="landing-footer-links">
            <h4>{t('landing.legal')}</h4>
            <Link to="/pravila-privatnosti">{t('landing.privacyRules')}</Link>
            <Link to="/uvjeti-koristenja">{t('landing.terms')}</Link>
            <Link to="/brisanje-racuna">{t('landing.deleteAccount')}</Link>
          </div>

          <div className="landing-footer-bottom">
            <span className="landing-footer-copy">
              © {new Date().getFullYear()} StatArena · statarena.app
            </span>
            <span className="landing-footer-ext">
              <a
                href="https://hr.wikipedia.org/wiki/Nogomet"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('landing.footballWiki')}
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://hns-cff.hr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('landing.footballFederation')}
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
